// =============================================================================
// Avatar Engine Store — Global Avatar Overlay State
// Manages the floating avatar that appears above all content app-wide.
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AvatarProfile {
  id: string;
  name: string;
  voiceId: string;        // links to voice profile (roger, bella, custom)
  originalImageUrl: string;
  processedImageUrl: string; // background-removed version
  bgMode: 'transparent' | 'green-screen' | 'blue-screen' | 'original';
  createdAt: string;
}

export type DockPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom';

interface AvatarState {
  // Overlay visibility
  isOverlayVisible: boolean;
  // Active avatar
  activeAvatarId: string | null;
  // Stored avatars
  avatars: AvatarProfile[];
  // Overlay geometry
  position: { x: number; y: number };
  size: { width: number; height: number };
  dockPosition: DockPosition;
  // Background display mode
  bgMode: 'transparent' | 'green-screen' | 'blue-screen' | 'original';
  // Speaking state (driven by TTS events)
  isSpeaking: boolean;
  amplitude: number; // 0..1

  // Actions
  showOverlay: () => void;
  hideOverlay: () => void;
  toggleOverlay: () => void;
  setActiveAvatar: (id: string | null) => void;
  addAvatar: (avatar: AvatarProfile) => void;
  removeAvatar: (id: string) => void;
  setPosition: (pos: { x: number; y: number }) => void;
  setSize: (size: { width: number; height: number }) => void;
  setDockPosition: (dock: DockPosition) => void;
  setBgMode: (mode: 'transparent' | 'green-screen' | 'blue-screen' | 'original') => void;
  setSpeaking: (speaking: boolean, amplitude?: number) => void;
  setAmplitude: (amplitude: number) => void;
}

export const useAvatarStore = create<AvatarState>()(
  persist(
    (set) => ({
      isOverlayVisible: false,
      activeAvatarId: null,
      avatars: [],
      position: { x: 0, y: 0 },
      size: { width: 220, height: 300 },
      dockPosition: 'bottom-right',
      bgMode: 'transparent',
      isSpeaking: false,
      amplitude: 0,

      showOverlay:   () => set({ isOverlayVisible: true }),
      hideOverlay:   () => set({ isOverlayVisible: false }),
      toggleOverlay: () => set((s) => ({ isOverlayVisible: !s.isOverlayVisible })),
      setActiveAvatar: (id) => set({ activeAvatarId: id }),
      addAvatar: (avatar) => set((s) => ({ avatars: [...s.avatars, avatar] })),
      removeAvatar: (id) => set((s) => ({ avatars: s.avatars.filter((a) => a.id !== id) })),
      setPosition: (pos) => set({ position: pos }),
      setSize: (size) => set({ size }),
      setDockPosition: (dock) => set({ dockPosition: dock }),
      setBgMode: (mode) => set({ bgMode: mode }),
      setSpeaking: (speaking, amplitude = 0) => set({ isSpeaking: speaking, amplitude }),
      setAmplitude: (amplitude) => set({ amplitude }),
    }),
    {
      name: 'r2.avatar-engine',
      // Only persist avatar profiles and settings; overlay visibility is always session-only
      partialize: (s) => ({
        avatars: s.avatars,
        activeAvatarId: s.activeAvatarId,
        dockPosition: s.dockPosition,
        size: s.size,
        bgMode: s.bgMode,
      }),
      onRehydrateStorage: () => (state) => {
        // Always start with overlay hidden — never restore from previous session
        if (state) {
          state.isOverlayVisible = false;
          state.isSpeaking = false;
          state.amplitude = 0;
        }
      },
    },
  ),
);
